import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { fetch } from "@insite/client-framework/ServerSideRendering";
import { shareEntity as shareEntityApi, ShareEntityApiParameter } from "@insite/client-framework/Services/EmailService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { ShareEntityModel } from "@insite/client-framework/Types/ApiModels";

export interface ShareEntityParameter {
    shareEntityModel: ShareEntityModel;
    url: string;
    onSuccess?: () => void;
    generateAttachmentFromWebpage?: boolean;
}

type HandlerType = ApiHandlerDiscreteParameter<ShareEntityParameter, ShareEntityApiParameter, ShareEntityModel>;

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        shareEntityModel: props.parameter.shareEntityModel,
        url: props.parameter.url,
    };
};



function buildPrintableHtml(state: ApplicationState): string {
  if (typeof window === "undefined") {
    return '<!DOCTYPE html><html><head><base href="/"/></head><body><div id="react-app"></div></body></html>';
  }

  const origin = window.location.origin;
  const head = document.head.cloneNode(true) as HTMLElement;
  head.querySelectorAll("script").forEach(s => s.remove());

  // Collect ALL styles - both inline style tags and convert link stylesheets to inline styles
  const allStyleTags = Array.from(document.querySelectorAll('style'));
  const headStyleTags = Array.from(head.querySelectorAll('style'));
  
  // Add any style tags that might have been missed during cloning
  allStyleTags.forEach(styleTag => {
    const styleId = styleTag.getAttribute('data-styled') || styleTag.id;
    const alreadyExists = headStyleTags.some(s => 
      (styleId && (s.getAttribute('data-styled') === styleId || s.id === styleId)) ||
      s.innerHTML === styleTag.innerHTML
    );
    if (!alreadyExists) {
      const clonedStyle = styleTag.cloneNode(true) as HTMLStyleElement;
      head.appendChild(clonedStyle);
    }
  });

  // Convert external link stylesheets to inline style tags by extracting CSS from CSSStyleSheet objects
  try {
    const styleSheets = Array.from(document.styleSheets);
    styleSheets.forEach((sheet) => {
      try {
        // Only process stylesheets that have cssRules (excludes cross-origin sheets we can't access)
        if (sheet.cssRules && sheet.cssRules.length > 0) {
          const cssRules = Array.from(sheet.cssRules).map(rule => rule.cssText);
          const cssText = cssRules.join('\n');
          
          // Check if this CSS is already in the head
          const alreadyExists = headStyleTags.some(s => s.innerHTML.includes(cssText.substring(0, 100)));
          
          if (!alreadyExists && cssText.length > 0) {
            const inlineStyle = document.createElement('style');
            inlineStyle.textContent = cssText;
            head.appendChild(inlineStyle);
          }
        } else if (sheet.href) {
          // For external stylesheets, keep the link tag with absolute URL
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = sheet.href.startsWith('http') ? sheet.href : origin + sheet.href;
          head.appendChild(link);
        }
      } catch (e) {
        // Cross-origin stylesheet - keep the original link if it exists
        if (sheet.href) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = sheet.href.startsWith('http') ? sheet.href : origin + sheet.href;
          head.appendChild(link);
        }
      }
    });
  } catch (e) {
    // Silently handle stylesheet processing errors
  }

  const main = document.querySelector("main");
  const appEl = document.getElementById("react-app");
  const source = (main ?? appEl ?? document.createElement("div"));
  const appClone = source.cloneNode(true) as HTMLElement;

  appClone.querySelectorAll("script").forEach(s => s.remove());

  const REMOVE_SELECTORS = [
    "form",
    'form[action*="activehosted.com"]',
    'script[src*="activehosted.com"]',
    '[id^="_form_"]',
    '._form',
    '._form_1',
    '[id^="snippet-wrapper_"]',
    'script[src*="wufoo.com"]',
    '[class*="newsletter"]',
    '[class*="subscribe"]',
    '.ToasterWrapper-sc-1eh8v4n',
    '[class*="Toaster"]',
    '[class*="drawer"]',
    '[class*="Scrim"]',
    '[role="status"][aria-live="polite"]',
    "header",
    "footer",
    '[role="banner"]',
    '[data-test-selector*="header"]',
    '[class*="overlay"]',
    '[class*="flyout"]',
    '[class*="menu"]',
    '[aria-hidden="true"]',
  ];
  REMOVE_SELECTORS.forEach(sel => appClone.querySelectorAll(sel).forEach(el => el.remove()));

  let logo = "";
  if (typeof document !== "undefined") {
    logo = document.querySelector(".home-page-link img")?.getAttribute("src") || "";
  }

  head.insertAdjacentHTML(
    "beforeend",
    `<style>
      @media print {
        @page { margin: 8mm 8mm 12mm 12mm; size: auto;  }
        body { margin: 0; }

         body > *:not(#react-app):not(#print-firstpage-header) {
          display: none !important; visibility: hidden !important;
        } 

         header, footer, nav {
          display: none !important; visibility: hidden !important;
        }

        #print-firstpage-header {
          position: absolute;
          top: 2mm;
          left: 8mm;
          right: auto;
          z-index: 9999;
          page-break-after: avoid;
          break-after: avoid-page;
          page-break-inside: avoid;
        }
        #print-firstpage-header img {
          max-height: 14mm;
          width: auto;
          display: block;
        }

        #react-app {
          margin-top: 18mm;
          margin-left: 2mm;
          page-break-before: auto !important;
          break-before: avoid-page !important;
        }

        #react-app > *:first-child {
          page-break-before: auto !important;
          break-before: avoid-page !important;
          margin-top: 0 !important;
        }

        footer ._form_1, footer form, footer [id^="_form_"], footer [class*="subscribe"], footer [class*="newsletter"],
        #_form_68EB49C2C7C32_, [id^="snippet-wrapper_"] {
          display: none !important;
          visibility: hidden !important;
        }
        form { display: none !important; visibility: hidden !important; }

        #react-app [style*="display:flex"], #react-app .flex, #react-app [class*="flex"],
        #react-app [style*="display:grid"],  #react-app .grid, #react-app [class*="grid"] {
          display: block !important;
        }

        .no-break, .section, .panel, .card, .line-item, .summary-block, .address-block, .totals-block,
        p, ul, ol, li, blockquote,
        h1, h2, h3, h4, h5, h6,
        img, figure, pre, code {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        h1, h2, h3 { page-break-after: avoid !important; break-after: avoid-page !important; }
        
        /* Allow first child to break naturally to prevent entire content from jumping to page 2 */
        #react-app > *:first-child {
          page-break-inside: auto !important;
          break-inside: auto !important;
        }

        table { page-break-inside: auto; border-collapse: collapse; width: 100%; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
        tr, td, th { page-break-inside: avoid !important; break-inside: avoid !important; }

        .cards, .items, [class*="columns"] { column-count: 1 !important; }

        #react-app *, #react-app *::before, #react-app *::after { overflow: visible !important; }
        #react-app [style*="max-height"], #react-app .max-h, #react-app .overflow-hidden,
        #react-app .overflow-auto, #react-app .overflow-scroll { max-height: none !important; overflow: visible !important; }

        img { max-width: 100% !important; height: auto !important; }
        [style*="transform"], .has-transform { transform: none !important; }

        p, li { orphans: 3; widows: 3; }

        #react-app .PageLayoutWrapper-sc-u6eb4b,
        #react-app .PageStyle-sc-mfj6r,
        #react-app .Wrapper-sc-4mgjmp,
        #react-app .GridWrapper-sc-1i0bb88,
        #react-app .GridOffset-sc-1rqhv8m,
        #react-app .GridItemStyle-sc-wm3igd,
        #react-app [class*="PageLayoutWrapper-"],
        #react-app [class*="PageStyle-"],
        #react-app [class*="Wrapper-"],
        #react-app [class*="GridWrapper-"],
        #react-app [class*="GridOffset-"],
        #react-app [class*="GridItemStyle-"] {
          page-break-inside: auto !important;
          break-inside: auto !important;
          height: auto !important;
          min-height: 0 !important;
        }

        .ToasterWrapper-sc-1eh8v4n,
        [class*="Toaster"],
        [class*="drawer"],
        [class*="Scrim"],
        [role="status"][aria-live="polite"] {
          display: none !important;
          visibility: hidden !important;
        }

        html, body { height: auto !important; min-height: 0 !important; }
        #react-app { margin-bottom: 0 !important; padding-bottom: 0 !important; }
        #react-app > *:last-child { margin-bottom: 0 !important; padding-bottom: 0 !important; }
        #react-app::after {
          content: "";
          display: block;
          height: 0;
          page-break-after: avoid !important;
          break-after: avoid-page !important;
        }
      }
    </style>`
  );

  const base = `<base href="${origin}/" />`;
  const bodyInner = appClone.innerHTML;
  const logoHtml = logo ? `<div id="print-firstpage-header"><img src="${logo}" alt="Logo" /></div>` : "";


  return `<!DOCTYPE html>
          <html lang="en">
          <head>
            ${base}
            ${head.innerHTML}
          </head>
          <body>
            ${logoHtml}
            <div id="react-app">${bodyInner}</div>
          </body>
          </html>`;
}

  

async function postCustomShare(model: {
    EmailTo: string;
    EmailFrom: string;
    Subject: string;
    Message: string;
    EntityId: string;
    EntityName: string;
    Html: string;
    Username: string;
}) {
    const resp = await fetch("/api/v1/customshare/shareEntityGenerateFromWebpage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(model),
    });

    if (!resp.ok) {
        throw new Error(`CustomShare failed. HTTP ${resp.status}`);
    }
}

export const SendDataToApi: HandlerType = async props => {
    const {
        parameter: { generateAttachmentFromWebpage },
        apiParameter,
    } = props;

    if (!generateAttachmentFromWebpage) {
        props.apiResult = await shareEntityApi(apiParameter);
        return;
    }

    const state = props.getState();
    const username = state.context.session?.userName ?? "";
    const html = buildPrintableHtml(state);

    const mappedModel = {
        EmailTo: (apiParameter.shareEntityModel as any).emailTo,
        EmailFrom: (apiParameter.shareEntityModel as any).emailFrom,
        Subject: (apiParameter.shareEntityModel as any).subject,
        Message: (apiParameter.shareEntityModel as any).message,
        EntityId: String((apiParameter.shareEntityModel as any).entityId ?? ""),
        EntityName: (apiParameter.shareEntityModel as any).entityName ?? "order",
        Html: html,
        Username: username,
    };

    await postCustomShare(mappedModel);

    props.apiResult = props.parameter.shareEntityModel;
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const chain = [PopulateApiParameter, SendDataToApi, ExecuteOnSuccessCallback];

const shareOrderEntity = createHandlerChainRunner(chain, "ShareOrderEntity");
export default shareOrderEntity;
